<template>
  <div class="walkin" v-if="bookingStationTime" :class="darkMode ? 'dark' : ''">
    <div class="modalcontent">
      <div class="selections">
        <div class="stationandexperience">
          <h2 class="f2">{{ getStationName(bookingStationTime.station.id) }}</h2>
          <h3 class="f5">{{ getExperienceName(bookingStationTime.experience.id) }}</h3>
        </div>
        <StartTime
          class="mobile"
          :otherTimesNumber="6"
          :booking="booking"
          :bookings="bookings"
          :location="location"
          :currentTime="currentTime"
          :startTime="startTime"
          @setStartingTime="setStartingTime"
          @setStartingTimeNow="setStartingTimeNow"
          @edit:booking="editBooking"
        />
        <StartTime
          class="desktop"
          :otherTimesNumber="10"
          :booking="booking"
          :bookings="bookings"
          :location="location"
          :currentTime="currentTime"
          :startTime="startTime"
          @setStartingTime="setStartingTime"
          @setStartingTimeNow="setStartingTimeNow"
          @edit:booking="editBooking"
        />

        <Tier
          v-if="bookingStationTime.tier"
          :location="location"
          :booking="booking"
          :bookings="bookings"
          :bookingStationTime="bookingStationTime"
          :tiers="getExperienceTiers(bookingStationTime.experience.id)"
          :startTime="startTime"
          :isOrganizationEducation="isOrganizationEducation"
          @tierAction="tier => tierAction(tier, bookingStationTime._id)"
          @edit:booking="editBooking"
        />
      </div>

      <div v-if="bookingConflict" class="conflict f5">
        <div>{{ $t('You have selected a setup that will cause a scheduling conflict.') }}</div>
        <div>{{ $t('You can still book this reservation, however.') }}</div>
      </div>

      <div class="line"></div>

      <div class="actions">
        <div class="left">
          <BookingPrice
            v-if="!isOrganizationEducation"
            :locationId="locationId"
            :bookingStationTimes="booking.bookingStationTimes"
            @toggleMarkAsPaid="toggleMarkAsPaid"
            @edit:booking="editBooking"
          />
        </div>
        <div class="right">
          <UIButton
            :ghost="true"
            class="editbooking"
            @click="goToBookingPage"
          >{{ $t("More Options") }}</UIButton>
          <UIButton
            :disabled="booking.bookingStationTimes.length === 0"
            @click="storeBooking"
            class="start"
          >
            <span v-if="booking._startNow">{{ $t("Start Now") }}</span>
            <span v-else>{{ $t("Create") }}</span>
          </UIButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from '@/core/store';
import { usePermissions } from '@/core/shared/setup/permissions';
import { useI18N } from '@/core/shared/localization';
import { useRouter } from '@/core/routes';
import { computed } from '@vue/composition-api';

import useBooking from './compositions/useBooking';

import StartTime from './components/StartTime';
import Tier from './components/Tier';
import BookingPrice from '@/location/price-components/BookingPrice';
import UIButton from '@/core/shared/components/ui/UIButton';

export default {
  components: {
    StartTime,
    Tier,
    BookingPrice,
    UIButton,
  },
  props: {
    locationId: {
      type: String,
      required: true,
    },
    preSelected: {
      type: Object,
      required: false,
    },
    bookingId: {
      type: String,
      default: 'new',
    },
    darkMode: {
      type: Boolean,
      required: false,
    },
  },
  setup ({ locationId, preSelected, bookingId, darkMode }) {
    const permissions = usePermissions();
    const store = useStore();
    const { t } = useI18N();
    const router = useRouter();
    const isOrganizationEducation = computed(() => permissions.education);

    const {
      location,
      bookings,
      currentTime,
      booking,
      startTime,
      experiences,
      stations,
      getExperienceTiers,

      bookingConflict,
      editBooking,
      setStartingTime,
      setStartingTimeNow,
      toggleMarkAsPaid,
      tierAction,
      storeBooking,
    } = useBooking(locationId, bookingId, preSelected, store, router, t);

    const bookingStationTime = computed(() => {
      return booking.value.bookingStationTimes[0];
    });

    function getExperienceName (experienceId) {
      return experiences.value.find(experience => experience.id === experienceId).title;
    }

    function getStationName (stationId) {
      return stations.value.find(station => station.id === stationId).title;
    }

    function goToBookingPage () {
      store.commit('setOverviewScrollPosition');
      router.push({
        name: 'booking',
        params: {
          bookingId: 'new',
          preSelected: booking,
          darkMode: darkMode,
        },
      });
    }

    return {
      location,
      bookings,
      currentTime,
      booking,
      startTime,
      experiences,
      stations,
      getExperienceTiers,

      bookingConflict,
      editBooking,
      setStartingTime,
      setStartingTimeNow,
      toggleMarkAsPaid,
      tierAction,
      storeBooking,

      isOrganizationEducation,

      bookingStationTime,

      getExperienceName,
      getStationName,
      goToBookingPage,
    };
  },
};
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.walkin {
  margin: 0 auto;

  & .modalcontent {
    & .selections {
      padding: var(--spacingSm);
      display: grid;
      grid-gap: var(--spacingMd);

      & .desktop {
        display: none;
      }

      @media (--tablet) {
        padding: var(--spacingSm) var(--spacingMd);

        & .desktop {
          display: block;
        }
        & .mobile {
          display: none;
        }
      }

      @media (--desktop) {
        padding: var(--spacingMd) var(--spacingLg);
      }

      & .stationandexperience {
        margin: 0;

        & h2 {
          margin: 0;
        }

        & h3 {
          margin: 0;
          color: var(--colorManatee);
        }
      }
    }

    & .line {
      height: 0.2rem;
      width: 100%;
      background-color: var(--colorCasper);
    }

    & .conflict {
      margin: 0;
      padding: var(--spacingXS);
      background-color: var(--colorErrorFlat);
      color: var(--colorWhite);
      text-align: center;
    }

    & .actions {
      justify-content: space-between;
      align-items: center;
      padding: var(--spacingSm);

      @media (--tablet) {
        width: 90vw;
        display: flex;
        width: auto;
        padding: var(--spacingSm) var(--spacingMd);
      }
      & .left {
        display: flex;
        align-items: center;
        flex: 1 1 auto;
        margin-bottom: var(--spacingSm);

        @media (--tablet) {
          margin-bottom: 0;
        }
      }

      & .right {
        align-items: center;
        flex: 1 1 auto;
        justify-content: flex-end;
        align-items: center;

        @media (--tablet) {
          display: flex;
        }

        & .editbooking {
          margin-bottom: var(--spacingSm);
          flex: 1 1 auto;
          width: 100%;

          @media (--tablet) {
            margin-right: var(--spacingMd);
            margin-bottom: 0;
          }
        }

        & .start {
          width: 100%;

          @media (--desktop) {
            flex: 1 1 auto;
          }
        }
      }
    }
  }
}
</style>

<style lang="postcss">
@import "core/shared/styles";

.flatpickr-time.flatpickr-input {
  font-size: 1.4rem;
  line-height: 2rem;
  font-family: var(--fontSansSerif);
  font-weight: 400;

  background: var(--colorLighterGrey);
  border-radius: var(--radius);
  width: 100%;
  border: 0.1rem solid var(--colorCasper);
  position: relative;
  box-shadow: none;
  font-weight: 700;
  padding: var(--spacingMd) 0;
  margin: 0;
  text-transform: none;
  appearance: none;

  &:disabled {
    background: var(--colorCasper);
  }

  &:focus {
    outline: none;
    background: rgb(238, 238, 238);
    /* background: color-mod(var(--colorLighterGrey) blackness(7%)); */
  }
}

.walkin.dark {
  background-color: var(--darkModeColorDarkBackground);
  color: var(--darkModeColorText);
}

.walkin.dark .modalcontent .selections .start-time .top .startnoworcustom .label {
  color: var(--darkModeColorText) !important;
}

.walkin.dark .modalcontent .selections .start-time .top .startnoworcustom .ui-button {
  background-color: var(--darkModeColorBackground) !important;
  color: var(--darkModeColorText) !important;
  &.-active {
  background-color: var(--darkModeColorLightBackground) !important;
  }
}

.walkin.dark .modalcontent .selections .start-time .top .other-times .label {
  color: var(--darkModeColorText) !important;
}

.walkin.dark .modalcontent .selections .start-time .top .other-times .ui-button {
  background-color: var(--darkModeColorBackground) !important;
  color: var(--darkModeColorText) !important;
  &.-active {
  background-color: var(--darkModeColorLightBackground) !important;
  }
}

.walkin.dark .modalcontent .selections .start-time .customstart span .label {
  color: var(--darkModeColorText) !important;
}

.walkin.dark .modalcontent .selections .start-time .customstart span .ui-timepicker {
  background-color: var(--darkModeColorBackground) !important;
  color: var(--darkModeColorText) !important;
  &.-active {
  background-color: var(--darkModeColorLightBackground) !important;
  }
}

.walkin.dark .modalcontent .selections .tiers .label {
  color: var(--darkModeColorText) !important;
}

.walkin.dark .modalcontent .selections .tiers .tiergrid .ui-button  {
  background-color: var(--darkModeColorBackground) !important;
  color: var(--darkModeColorText) !important;
  &.-active {
  background-color: var(--darkModeColorLightBackground) !important;
  }
}

.walkin.dark .modalcontent .selections .tiers .customtierinputs .ui-input  {
  background-color: var(--darkModeColorBackground) !important;
  color: var(--darkModeColorText) !important;
  &.-active {
  background-color: var(--darkModeColorLightBackground) !important;
  }
}

.walkin.dark .modalcontent .actions .left .booking-price .location-price .taxrow .taxes {
  color: var(--darkModeColorText) !important;
}

.walkin.dark .modalcontent .actions .left .booking-price div .ui-checkbox .box-and-title .title {
  color: var(--darkModeColorText) !important;
}

.walkin.dark .modalcontent .actions .right .editbooking   {
  background-color: var(--darkModeColorBackground) !important;
  color: var(--darkModeColorText) !important;
}
</style>
